import { captureEvent, identifyUser, isPostHogActive, resetUser } from '../lib/posthog';
import { Sentry } from '../lib/sentry';
import type { AnalyticsEventName, PropsFor } from './events';

export type { AnalyticsEvent, AnalyticsEventName, PropsFor } from './events';

/**
 * Observability shim. Call sites use `track(...)`, `capture(...)`, and
 * `identify(...)`; the underlying transport is decided here.
 *
 * The shim sits in front of two SDKs:
 * - **PostHog** (`src/lib/posthog.ts`) — product analytics; flips on when
 *   `VITE_POSTHOG_KEY` is provisioned. `track` → `posthog.capture`,
 *   `identify` → `posthog.identify` + `posthog.reset`.
 * - **Sentry** (`src/lib/sentry.ts`) — error reporting; flips on when
 *   `VITE_SENTRY_DSN` is provisioned. `capture` → `Sentry.captureException`.
 *
 * In dev (no keys) we mirror to the console so flows are debuggable;
 * in prod (keys absent) we no-op silently. Behavior is gated on
 * `import.meta.env.DEV` for the console mirror and on each SDK's
 * `isActive()` for the network path.
 */

const devEcho = import.meta.env.DEV;

export function track<N extends AnalyticsEventName>(name: N, props: PropsFor<N>): void {
  if (devEcho) console.info('[analytics]', name, props);
  if (isPostHogActive()) {
    // Re-cast to a permissive shape — PostHog's capture accepts any string
    // keys. The compile-time vocabulary lives in `events.ts`; this boundary
    // crosses from our taxonomy to PostHog's wire format.
    captureEvent(name, props as Record<string, unknown>);
  }
}

/**
 * Capture an unexpected error. Routed to Sentry when configured; in dev
 * we also mirror to the console so React's error overlay isn't the only
 * place an error shows up.
 *
 * Context lands in Sentry as `extra` (queryable but not searchable on
 * the high-cardinality fields). Use tags via Sentry directly for
 * low-cardinality fields you want to filter by.
 */
export function capture(error: unknown, context?: Record<string, unknown>): void {
  if (devEcho) console.error('[capture]', error, context ?? {});
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Identify the active user. Bound after Clerk sign-in completes via
 * `useClerkPostHogBridge` in `src/lib/posthog.ts` — but call sites
 * that have a workspace id (e.g. session-bootstrap) can still tag
 * analytics via this hook for backward compatibility.
 *
 * Passing `null` resets identity (sign-out path).
 */
export function identify(workspaceId: string | null): void {
  if (devEcho) console.info('[identify]', { workspaceId });
  if (workspaceId === null) {
    resetUser();
    return;
  }
  // Workspace-as-identity for backward compatibility with the legacy
  // shim. The Clerk bridge is the primary identity path; this is for
  // call sites that only know the workspace.
  identifyUser(`ws_${workspaceId}`, { workspaceId });
}
